import clsx from 'clsx';
import { useState, type MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { Duration } from 'date-fns';

import DateRangePopup from '~/shared/components/DateRangePopup';
import SimpleDropdown from '~/shared/components/SimpleDropdown';
import CalendarIcon from '~/shared/assets/icons/calendar.svg';
import { getDateFilterDates } from '~/shared/utils/getDateFilterDates';

import { formatDate } from '../utils/formatNumber';

import { CUSTOM_DATES_TITLE } from './dateFilterMenuItems';

function ChartFilterDropdown<
  T extends { title: string; duration?: { start: Duration; end?: Duration } },
>({
  items,
  onSelect,
  selectedItem,
  align = 'top-right',
  fullWidth = false,
  portal = false,
  delegateCustomDateCalendar = false,
  onDelegatedCustomDateOpen,
  menuButtonRef,
}: {
  onSelect: (selection: T) => void;
  items?: T[];
  selectedItem: T;
  align?: 'top-right' | 'top-left';
  fullWidth?: boolean;
  portal?: boolean;
  delegateCustomDateCalendar?: boolean;
  onDelegatedCustomDateOpen?: () => void;
  menuButtonRef?: MutableRefObject<HTMLButtonElement | null>;
}) {
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const isCustomDateSelected = selectedItem.title?.includes(CUSTOM_DATES_TITLE);
  const [startDate, endDate] = getDateFilterDates(selectedItem?.duration, selectedItem.title);

  const itemTitles = items?.map((item) => item.title) ?? [];
  const selectedTitle = selectedItem?.title ?? '';

  const displayLabel = isCustomDateSelected
    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
    : selectedItem?.title;

  const handleCustomDateSelect = (startDate: Date, endDate: Date) => {
    const start = startDate.toISOString();
    const end = endDate.toISOString();
    const customItem = {
      title: `${CUSTOM_DATES_TITLE}|${start}|${end}`,
    };

    onSelect(customItem as T);
    setIsCustomDateOpen(false);
  };

  const handleSelect = (title: string) => {
    if (title === CUSTOM_DATES_TITLE) {
      if (delegateCustomDateCalendar) {
        onDelegatedCustomDateOpen?.();
      } else {
        setIsCustomDateOpen(true);
      }
      return;
    }

    const item = items?.find((entry) => entry.title === title);
    if (item) {
      onSelect(item);
    }
  };

  const customDatePopup = (
    <div
      className={clsx(
        portal
          ? 'fixed z-[99999] w-fit max-w-[calc(100vw-16px)] rounded-md shadow-datepicker border border-primary-70 sm:px-0'
          : 'absolute z-[60] top-[100%] mt-1 rounded-md w-fit shadow-datepicker border border-primary-70 sm:px-0',
        !portal && (align === 'top-right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left')
      )}
      style={
        portal
          ? {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxHeight: 'calc(100vh - 16px)',
              overflowY: 'auto',
            }
          : undefined
      }
    >
      <DateRangePopup
        startDateStr={startDate}
        endDateStr={endDate}
        onSubmit={handleCustomDateSelect}
        onClose={() => setIsCustomDateOpen(false)}
      />
    </div>
  );

  return (
    <div className={clsx('relative', fullWidth && 'w-full')}>
      <SimpleDropdown
        items={itemTitles}
        selectedItem={selectedTitle}
        displayLabel={displayLabel}
        onSelect={handleSelect}
        align={align}
        fullWidth={fullWidth}
        portal={portal}
        menuButtonRef={menuButtonRef}
        onMenuButtonClick={() => {
          if (isCustomDateOpen) {
            setIsCustomDateOpen(false);
          }
        }}
        isItemSelected={(title) =>
          title === selectedItem?.title ||
          (title === CUSTOM_DATES_TITLE && Boolean(isCustomDateSelected))
        }
        renderItemEnd={(title) =>
          title === CUSTOM_DATES_TITLE ? (
            <CalendarIcon
              width="24px"
              className="size-6 stroke-[#2D2D32] group-hover:stroke-[#2D2D32] transition-colors duration-200"
            />
          ) : null
        }
        className={fullWidth ? 'w-full' : undefined}
        menuButtonClassName={clsx(
          fullWidth ? 'w-full' : 'min-w-[290px]',
          'px-2 py-3 h-9! 4xl:h-11! hover:border-primary-300 active:border-primary-100'
        )}
        menuItemsClassName={clsx(
          portal ? 'z-[99999]' : 'z-60',
          portal && fullWidth
            ? 'w-[var(--button-width)]'
            : fullWidth
              ? 'w-full'
              : 'w-60 2xl:w-[290px]',
          'mt-2.5 side-panel-scrollbar focus:outline-none',
          !portal && 'absolute top-[100%]'
        )}
        menuItemClassName=""
      />

      {isCustomDateOpen &&
        (portal && typeof document !== 'undefined'
          ? createPortal(customDatePopup, document.body)
          : customDatePopup)}
    </div>
  );
}

export default ChartFilterDropdown;
